// src/components/SearchableSelect.js
//
// Type-to-filter picker over a fixed list of names (e.g. active coordinators).
// Typing narrows the list; click or Enter picks. It only ever reports a value
// FROM `options` — whatever was typed but not picked is thrown away on blur —
// so free-text variants ("Jalak" / "jalak" / "Jalak P") can't reach the data.
//
// `value` is shown in the box when not searching. A current value that is not
// in `options` (e.g. a coordinator since deactivated) is still shown, never
// cleared. The menu is `position: fixed` under the box, so a parent with
// overflow-hidden or a scrolling modal can't clip it.

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UserCircle2, Search, ChevronDown } from "lucide-react";

const norm = (s) => String(s || "").trim().toLowerCase();

const defaultInputCls =
  "w-full rounded-xl bg-white/[0.04] border border-white/10 pl-9 pr-9 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition disabled:opacity-60";

const SearchableSelect = ({
  value = "",
  options = [],
  onChange,
  placeholder = "Type to search…",
  loading = false,
  disabled = false,
  noMatchText = "No matches.",
  emptyText = "Nothing to choose from.",
  inputClassName = defaultInputCls,
  // Optional first choice that isn't a name — "— none —" (value "") or
  // "All coordinators" (value "All"). Shown in the box when it's the value.
  clearOption = null,
  // Optional dimmed note after a name in the list, e.g. " (no email)".
  optionHint = null,
  // Smaller box for toolbars (hides the icons, keeps the behaviour).
  compact = false,
  // Icon shown before each name in the list. Defaults to the person icon every
  // coordinator picker uses; the study picker passes its own.
  optionIcon: OptionIcon = UserCircle2,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();

  const matches = useMemo(() => {
    const q = norm(query);
    return q ? options.filter((n) => norm(n).includes(q)) : options;
  }, [options, query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  // Keep the fixed menu under the box while open (scroll / resize).
  const measure = useCallback(() => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
  }, []);
  useLayoutEffect(() => {
    if (!open) return undefined;
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, measure]);

  // Close on a click anywhere else; unpicked text is discarded.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (inputRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, close]);

  useEffect(() => setActive(0), [query]);

  const pick = (name) => {
    onChange(name);
    close();
    inputRef.current?.blur();
  };

  const busy = loading || disabled;
  const isClear = clearOption && value === clearOption.value;
  const shown = isClear ? clearOption.label : value;
  // The clear choice is offered only while not filtering by a typed query.
  const showClear = clearOption && !norm(query);
  const itemCount = matches.length + (showClear ? 1 : 0);
  // Keyboard index 0 is the clear choice when it's shown.
  const itemAt = (i) => (showClear ? (i === 0 ? clearOption.value : matches[i - 1]) : matches[i]);

  return (
    <div className="relative">
      {!compact && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <Search className="w-4 h-4 text-slate-500" />
        </span>
      )}
      <input
        ref={inputRef}
        type="text"
        // While searching show the query; otherwise the chosen value.
        value={open ? query : shown}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActive((a) => Math.min(a + 1, Math.max(itemCount - 1, 0)));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === "Enter") {
            // Never submit a surrounding form from here.
            e.preventDefault();
            if (open && active < itemCount) pick(itemAt(active));
          } else if (e.key === "Escape" || e.key === "Tab") {
            close();
          }
        }}
        placeholder={loading ? "Loading…" : shown || placeholder}
        disabled={busy}
        role="combobox"
        aria-controls={listId}
        aria-expanded={open}
        aria-autocomplete="list"
        className={inputClassName}
      />
      <ChevronDown
        className={`absolute top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none ${
          compact ? "right-2 w-3 h-3" : "right-3 w-3.5 h-3.5"
        }`}
      />
      {open &&
        !busy &&
        rect &&
        createPortal(
          <ul
            ref={menuRef}
            id={listId}
            role="listbox"
            style={{ position: "fixed", top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 220), zIndex: 1000 }}
            className="coordinator-picker-menu max-h-56 overflow-y-auto rounded-xl border border-white/10 bg-slate-950/95 backdrop-blur-xl shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7)] p-1"
          >
            {showClear && (
              <li role="option" aria-selected={isClear}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(clearOption.value);
                  }}
                  onMouseEnter={() => setActive(0)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left text-sm italic transition ${
                    active === 0 ? "bg-purple-500/20 text-white" : "text-slate-400 hover:bg-white/[0.06]"
                  }`}
                >
                  {clearOption.label}
                </button>
              </li>
            )}
            {matches.length === 0 ? (
              <li className="px-3 py-2 text-xs text-slate-500">{options.length ? noMatchText : emptyText}</li>
            ) : (
              matches.map((name, i) => {
                const idx = showClear ? i + 1 : i;
                const hint = optionHint ? optionHint(name) : "";
                return (
                  <li key={name} role="option" aria-selected={name === value}>
                    <button
                      type="button"
                      // mousedown, so the pick lands before the outside-click close.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pick(name);
                      }}
                      onMouseEnter={() => setActive(idx)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left text-sm transition ${
                        idx === active ? "bg-purple-500/20 text-white" : "text-slate-200 hover:bg-white/[0.06]"
                      }`}
                    >
                      <OptionIcon className="w-4 h-4 text-slate-400 shrink-0" />
                      {name}
                      {hint && <span className="text-[11px] text-slate-500">{hint}</span>}
                    </button>
                  </li>
                );
              })
            )}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default SearchableSelect;
